/**
 * Sidebar navigation item computation (Sidebar.jsx `getMenuItems`), extracted to a
 * dependency-free module so it is unit-testable without mounting antd/MSAL — mirrors the
 * extraction pattern already used for utils/workspaceStepRestore.js.
 *
 * Returns plain-data item descriptors (key / label / type / danger) with NO icon JSX —
 * Sidebar.jsx attaches the actual icon (via its `iconMap`, and the taxonomy-pending `Badge`
 * wrapper for MASTER_DATA, keyed off `taxonomyPendingCount` directly) when rendering, since
 * that requires the antd / @ant-design/icons imports this module intentionally does not
 * carry. Item PRESENCE never depended on `taxonomyPendingCount` (only its icon's dot did),
 * so this module's signature omits that param.
 *
 * Backlog #36/#37 regression guard (commit 09466d7): an Advisor only gains EVIDENCE +
 * DOCUMENT_LIBRARY nav items once a project is active — see the inline comment preserved
 * below (unchanged from Sidebar.jsx).
 *
 * Mechanical, behavior-preserving extraction — no logic changed from the fixed shape in
 * Sidebar.jsx.
 */
import { Role, View } from '../constants/roles';

export function getMenuItemKeys(user, activeProject, { trainingAvailable } = {}) {
    if (!user) return [];
    // Training unlocks at Stage 7 (API-enforced); an explicit false from the availability read hides the entry.
    const trainingOpen = trainingAvailable !== false;

    // Prolense support staff (backend `support_agent`): the console only. platform_admin keeps the full admin nav.
    if (user.role === Role.SUPPORT_AGENT) {
        return [{ key: View.SUPPORT, label: 'Support Console' }];
    }

    // Company Delegates always see a fixed nav
    if (user.role === Role.COMPANY_DELEGATE) {
        return [
            { key: View.TASK_CENTER, label: 'Tasks' },
            // Training (Sheet-2 row 9) — "My training"; project-scoped, so only inside a project.
            activeProject && trainingOpen && { key: View.TRAINING, label: 'Training' },
            { key: View.MEETINGS, label: 'Meetings' },
        ].filter(Boolean);
    }

    // Advisor — portfolio shell (same capabilities as LD minus admin-only items).
    // Backlog #36/#37: the API already authorizes 'advisor' on document-library +
    // evidence-register reads via project membership, and on evidence edits during
    // the 'advisor_review' stage (see canEditEvidenceInState in
    // registerRouteHelpers.ts) — this branch used to return before ever reaching the
    // activeProject block below that adds these items for LA/SPOC, so an Advisor had
    // no nav path to either view. Gate on activeProject: both pages render nothing
    // without a project (App.jsx currentView switch).
    if (user.role === Role.ADVISOR) {
        const items = [
            { key: View.DASHBOARD, label: 'Advisor Control Plane' },
            { key: View.TASK_CENTER, label: 'Tasks' },
            { key: View.POLICY_LIBRARY, label: 'Policy Hub' },
            // Regulatory library (Sheet-2 rows 8 + 10): GET /regulatory-content allows advisor; published items only.
            { key: View.REGULATORY_LIBRARY, label: 'Regulatory Library' },
        ];

        if (activeProject) {
            items.push(
                // Dashboard Redesign + SPOC Visibility (backlog Sheet-1 rows 23/33) —
                // GET /api/v1/projects/:id/dashboard already filters to what an Advisor may see.
                { key: View.PROJECT_DASHBOARD, label: 'Dashboard' },
                { key: View.EVIDENCE, label: 'Evidence' },
                { key: View.DOCUMENT_LIBRARY, label: 'Document Library' },
                // Software QA results (Sheet-2 toolkit #10) — sqa.service.ts allows lead_advisor + advisor only.
                { key: View.SQA_RESULTS, label: 'SQA Results' },
                // 'Which authorities apply?' (Sheet-2 rows 8 + 10): applicable-regulations needs project membership.
                { key: View.PROJECT_REGULATIONS, label: 'Applicable Regulations' },
                // Training authoring + history (Sheet-2 row 9); published-template lookup (Sheet-2 row 2).
                trainingOpen && { key: View.TRAINING, label: 'Training' },
                { key: View.LIBRARY_TEMPLATES, label: 'Template Library' },
            );
        }

        items.push({ key: View.MEETINGS, label: 'Meetings' });

        return items.filter(Boolean);
    }

    // Inside a project
    if (activeProject) {
        const items = [];
        if (user.role === Role.ADMIN) {
            items.push({ key: View.PROJECT_MEMBERS, label: 'Members' });
            // The API returns the operational dashboard to platform_admin (and the page offers Refresh).
            items.push({ key: View.PROJECT_DASHBOARD, label: 'Dashboard' });
            items.push({ key: View.TASK_CENTER, label: 'Tasks' });
        } else {
            items.push(
                { key: View.PROJECT_WORKSPACE, label: 'Workbench' },
                // Dashboard Redesign + SPOC Visibility (backlog Sheet-1 rows 23/33) — the
                // "generate shareable link" action lives on this page, SPOC-only within it.
                { key: View.PROJECT_DASHBOARD, label: 'Dashboard' },
                { key: View.PROJECT_MEMBERS, label: 'Members' },
                { key: View.EVIDENCE, label: 'Evidence' },
                { key: View.DOCUMENT_LIBRARY, label: 'Document Library' },
                { key: View.MEETINGS, label: 'Meetings' },
            );
        }

        if (user.role === Role.LEAD_ADVISOR) items.push({ key: View.SQA_RESULTS, label: 'SQA Results' });
        // Regulatory library + project applicability (Sheet-2 rows 8 + 10): reads are LA / Advisor / SPOC, never the Delegate;
        // the project-level 'which authorities apply' panel is for the advising personas only.
        if (user.role === Role.LEAD_ADVISOR || user.role === Role.COMPANY_SPOC) items.push({ key: View.REGULATORY_LIBRARY, label: 'Regulatory Library' });
        if (user.role === Role.LEAD_ADVISOR) items.push({ key: View.PROJECT_REGULATIONS, label: 'Applicable Regulations' });
        // Support stays reachable inside a project so a ticket can be linked to it (RaiseTicketModal).
        if (user.role === Role.LEAD_ADVISOR || user.role === Role.COMPANY_SPOC) items.push({ key: View.SUPPORT, label: 'Support' });
        // Training (Sheet-2 row 9): Lead Advisor authors/publishes, SPOC takes it; a platform admin has no training surface.
        if (user.role !== Role.ADMIN && trainingOpen) items.push({ key: View.TRAINING, label: 'Training' });
        // Published-template lookup (Sheet-2 row 2): Stage-7 drafting personas only (LA here; Advisor above).
        if (user.role === Role.LEAD_ADVISOR) items.push({ key: View.LIBRARY_TEMPLATES, label: 'Template Library' });

        // Add exit project divider + button
        items.push(
            { type: 'divider' },
            { key: 'exit_project', label: 'Exit Project', danger: true },
        );
        return items;
    }

    // Top-level nav (no project selected)
    return [
        {
            key: View.DASHBOARD,
            label: user.role === Role.COMPANY_SPOC ? 'Company View' : 'Control Plane',
        },
        user.role === Role.ADMIN && { key: View.LEAD_ADVISORS, label: 'Lead Advisors' },
        user.role === Role.ADMIN && { key: View.ADMINISTRATORS, label: 'Administrators' },
        // LA Onboarding (backlog Sheet-2 row 1, Workstream A). Lead-Advisor-only nav entry —
        // the documented backend contract (GET/POST/PATCH /api/v1/me/advisory-firm) is scoped
        // to the calling Lead Advisor only; there is no admin-scoped route to view a SPECIFIC
        // LA's firm yet, so a platform_admin nav entry is deliberately not added here. Flagged
        // as an open follow-up rather than guessed at — see handoff notes.
        user.role === Role.LEAD_ADVISOR && { key: View.ADVISORY_FIRM, label: 'My Firm' },
        // ONE Integrations hub (connections, Pathkey, platform feed keys, Open API keys). Inside the page each
        // card/tab is gated by the persona the API enforces; the nav entry is for the two roles that have any.
        [Role.LEAD_ADVISOR, Role.ADMIN].includes(user.role) && { key: View.INTEGRATIONS, label: 'Integrations' },
        user.role !== Role.ADMIN && { key: View.TASK_CENTER, label: 'Tasks' },
        user.role === Role.ADMIN && { key: View.QUESTION_BANK, label: 'Question Bank' },
        // Curator (Sheet-2 row 3): every /admin/question-bank route is platform_admin-only (PRD s2.2).
        user.role === Role.ADMIN && { key: View.QUESTION_BANK_CURATOR, label: 'Question Bank Curator' },
        [Role.ADMIN, Role.LEAD_ADVISOR, Role.ADVISOR].includes(user.role) && {
            key: View.POLICY_LIBRARY,
            label: 'Policy Hub',
        },
        // Library curator console (Sheet-2 row 2): every /admin/library-templates route is platform_admin-only.
        user.role === Role.ADMIN && { key: View.LIBRARY_TEMPLATES, label: 'Template Library' },
        user.role === Role.ADMIN && { key: View.MASTER_DATA, label: 'Control Taxonomy' },
        user.role === Role.ADMIN && { key: View.VULNERABILITY_MASTER, label: 'Vulnerability Database' },
        // Regulatory Database master (Sheet-2 rows 8 + 10): every mutation and admin read is platform_admin-only (PRD s2.2).
        user.role === Role.ADMIN && { key: View.REGULATORY_DATABASE, label: 'Regulatory Database' },
        // Read-only library for the personas the API allows; the Delegate gets 403 so no entry.
        [Role.LEAD_ADVISOR, Role.COMPANY_SPOC].includes(user.role) && { key: View.REGULATORY_LIBRARY, label: 'Regulatory Library' },
        // Prolense support tickets (Sheet-2 row 12): platform_admin console; LA / SPOC raise and follow
        // their own tickets. Mirrors supportTicket.routes.ts (admin actions route-gated; reporter-own rows).
        [Role.ADMIN, Role.LEAD_ADVISOR, Role.COMPANY_SPOC].includes(user.role) && {
            key: View.SUPPORT,
            label: user.role === Role.ADMIN ? 'Support Console' : 'Support',
        },
        { key: View.MEETINGS, label: 'Meetings' },
    ].filter(Boolean);
}

/**
 * Top-level (no project) nav grouping. Only the roles with a crowded flat list are grouped;
 * Advisor / SPOC / Delegate / Support stay flat. Grouping is presentation only: it is applied to the
 * already role-gated output of getMenuItemKeys, so it can never add a destination a role lacked, and
 * any gated item not named below is appended flat rather than dropped.
 */
const GROUP_PLANS = {
    [Role.ADMIN]: [
        { key: 'group:overview', label: 'Overview', views: [View.DASHBOARD, View.MEETINGS] },
        { key: 'group:people', label: 'People & Access', views: [View.LEAD_ADVISORS, View.ADMINISTRATORS, View.INTEGRATIONS] },
        {
            key: 'group:content',
            label: 'Content & Masters',
            views: [
                View.QUESTION_BANK, View.QUESTION_BANK_CURATOR, View.LIBRARY_TEMPLATES, View.POLICY_LIBRARY,
                View.MASTER_DATA, View.VULNERABILITY_MASTER, View.REGULATORY_DATABASE,
            ],
        },
        { key: 'group:support', label: 'Support', views: [View.SUPPORT] },
    ],
    [Role.LEAD_ADVISOR]: [
        { key: 'group:overview', label: 'Overview', views: [View.DASHBOARD, View.TASK_CENTER, View.MEETINGS] },
        { key: 'group:firm', label: 'My Firm & Integrations', views: [View.ADVISORY_FIRM, View.INTEGRATIONS] },
        { key: 'group:libraries', label: 'Libraries', views: [View.POLICY_LIBRARY, View.REGULATORY_LIBRARY] },
        { key: 'group:support', label: 'Support', views: [View.SUPPORT] },
    ],
};

// In-project plans (Delegate and platform admin have few items and stay flat). Exit Project + its divider are not
// named here, so they are appended flat after the groups, still last.
const PROJECT_GROUPS = [
    { key: 'group:work', label: 'Work', views: [View.PROJECT_WORKSPACE, View.PROJECT_DASHBOARD, View.DASHBOARD, View.TASK_CENTER, View.TRAINING] },
    { key: 'group:evidence', label: 'Evidence & Documents', views: [View.EVIDENCE, View.DOCUMENT_LIBRARY, View.SQA_RESULTS] },
    { key: 'group:team', label: 'People', views: [View.PROJECT_MEMBERS, View.MEETINGS] },
    {
        key: 'group:reference',
        label: 'Reference',
        views: [View.POLICY_LIBRARY, View.REGULATORY_LIBRARY, View.PROJECT_REGULATIONS, View.LIBRARY_TEMPLATES],
    },
    { key: 'group:support', label: 'Support', views: [View.SUPPORT] },
];
const PROJECT_PLANS = {
    [Role.LEAD_ADVISOR]: PROJECT_GROUPS,
    [Role.ADVISOR]: PROJECT_GROUPS,
    [Role.COMPANY_SPOC]: PROJECT_GROUPS,
};

/**
 * Returns antd-shaped items: `{ key, label, children: [...] }` groups (collapsible submenus) plus flat
 * items. A group with a single visible child is rendered flat (a submenu wrapping one link is just an
 * extra click). Inside a project only LA / Advisor / SPOC are grouped.
 */
export function getGroupedMenuItems(user, activeProject, opts) {
    const flat = getMenuItemKeys(user, activeProject, opts);
    const plan = user ? (activeProject ? PROJECT_PLANS[user.role] : GROUP_PLANS[user.role]) : null;
    if (!plan) return flat;
    const byKey = new Map(flat.map((i) => [i.key, i]));
    const placed = new Set();
    const out = [];
    for (const g of plan) {
        const children = g.views.map((v) => byKey.get(v)).filter(Boolean);
        children.forEach((c) => placed.add(c.key));
        if (children.length === 1) out.push(children[0]);
        else if (children.length > 1) out.push({ key: g.key, label: g.label, children });
    }
    flat.forEach((i) => { if (!placed.has(i.key)) out.push(i); });
    return out;
}

/** Group key containing `view`, or null (flat item / not found). */
export function findGroupKeyForView(items, view) {
    const g = (items || []).find((i) => Array.isArray(i.children) && i.children.some((c) => c.key === view));
    return g ? g.key : null;
}

const openGroupsStorageKey = (user) => `policylens.nav.openGroups.${user?.id || user?.email || user?.role || 'anon'}`;

export function loadOpenGroups(user, storage) {
    try {
        const s = storage || (typeof window !== 'undefined' ? window.localStorage : null);
        const v = JSON.parse(s.getItem(openGroupsStorageKey(user)));
        return Array.isArray(v) ? v.filter((k) => typeof k === 'string') : null;
    } catch (e) {
        return null;
    }
}

export function saveOpenGroups(user, keys, storage) {
    try {
        const s = storage || (typeof window !== 'undefined' ? window.localStorage : null);
        s.setItem(openGroupsStorageKey(user), JSON.stringify(keys));
    } catch (e) { /* storage unavailable: nav still works, just not remembered */ }
}

/**
 * Accordion rule: at most one top-level group open. `preferred` (the group holding the active view)
 * wins when it is among `keys`; otherwise the last key (most recently opened) is kept.
 */
export function normaliseOpenGroups(keys, preferred) {
    const list = Array.isArray(keys) ? keys.filter((k) => typeof k === 'string') : [];
    if (list.length === 0) return [];
    if (preferred && list.includes(preferred)) return [preferred];
    return [list[list.length - 1]];
}

/** antd `onOpenChange` hands the full next key list; opening B while A is open yields [B], closing yields []. */
export function nextOpenGroups(prevKeys, nextKeys) {
    const prev = Array.isArray(prevKeys) ? prevKeys : [];
    const added = (nextKeys || []).filter((k) => !prev.includes(k));
    return added.length ? [added[added.length - 1]] : [];
}
